import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { getUnitVerdicts, getMyFeedback } from 'features/evals3/api/evals3Api';
import Eval3VerdictFeedback from 'features/evals3/components/Eval3VerdictFeedback';
import { decisionText } from 'features/evals3/components/decision';
import { markedWrongPrompt } from 'features/evals3/components/markedWrongPrompt';
import { openAsk } from 'shared/askDraft';
import { tableHeaderRowClass, tableHeaderCellClass, tableDataCellClass, tableRowClass } from 'features/activity/styles/dataTableStyles';

/**
 * The new evals' verdicts on one run, span or conversation (TRO-451).
 *
 * Its OWN panel, mounted beside the current evals' list in the trace views
 * and never inside it — so deleting the old system deletes that list and
 * nothing here. A table like the older evals': one row per eval, its
 * result as a dot and the name the eval gave that ending ("Great answer",
 * "Hid a failed tool"), and the thumbs. No time, no version, no score on
 * the outside — open a row and its walk is a small table: each step, what it
 * answered, and what it measured (confidence, similarity, claims supported),
 * then the result. The full story — reasoning, evidence, context — is on the
 * eval's own results page, a click away.
 * The newest verdict per eval; a run's spans follow grouped under their
 * names, a conversation's runs under their turns.
 *
 * Renders nothing when there are no v3 verdicts, so a team that has not
 * built one sees the trace views exactly as before.
 */

/**
 * What a verdict says, in one cell: a dot (green pass, red fail, amber waiting,
 * orange errored) and the name its eval gave that ending — "Great answer",
 * "Hid a failed tool" — or Pass / Fail when the ending has no name of its own.
 */
const DOT = { passed: 'bg-success', failed: 'bg-danger', waiting: 'bg-info', errored: 'bg-warning', other: 'bg-[color:var(--border-strong)]' };

function outcomeOf(v) {
  if (v.state === 'resolved') return v.passed ? 'passed' : 'failed';
  if (v.state === 'pending') return 'waiting';
  if (v.state === 'errored') return 'errored';
  return 'other';
}

export function VerdictChip({ v }) {
  const outcome = outcomeOf(v);
  const end = (v.path || [])[(v.path || []).length - 1];
  const named = v.state === 'resolved' && end && v.node_names?.[end] && v.node_names[end] !== end ? v.node_names[end] : null;
  const label = {
    passed: named || 'Pass', failed: named || 'Fail', waiting: 'Waiting for a person', errored: 'Errored', other: v.state,
  }[outcome];
  const tone = { passed: 'text-success', failed: 'text-danger', waiting: 'text-info', errored: 'text-warning', other: 'text-[color:var(--text-muted)]' }[outcome];
  // What was decided — a score, a category, true/false — beside the ending's name.
  const decision = v.state === 'resolved' ? decisionText(v) : null;
  return (
    <span data-testid="eval3-verdict-chip" className="flex w-full min-w-0 items-center gap-1.5 overflow-hidden" title={[label, decision, v.reason_code || v.state_reason].filter(Boolean).join(' · ')}>
      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${DOT[outcome]}`} />
      <span className={`truncate text-[11.5px] font-medium ${tone}`}>{label}</span>
      {decision && <span className="shrink-0 font-mono text-[11px] tabular-nums text-[color:var(--text-secondary)]" data-testid="eval3-decision">{decision}</span>}
      <span className="sr-only">{outcome === 'passed' ? '(pass)' : outcome === 'failed' ? '(fail)' : ''}</span>
    </span>
  );
}

/** A step's answer and what it measured, in two short cells. */
const isNum = (x) => typeof x === 'number' && Number.isFinite(x);
function fmt(x) {
  if (x === null || x === undefined) return '—';
  if (isNum(x)) return Number.isInteger(x) ? String(x) : String(Number(x.toFixed(3)));
  if (typeof x === 'object') return JSON.stringify(x);
  return String(x);
}
const two = (x) => Number(x).toFixed(2);

export function stepCells(step) {
  const out = step.output || {};
  const value = step.value !== undefined ? step.value : out.value;
  const d = (step.details !== undefined ? step.details : out.details) || {};
  if (step.error || step.state === 'errored') return { outcome: 'Errored', tone: 'text-warning', metric: null, title: step.error || undefined };
  if (step.state === 'pending') return { outcome: 'Waiting for a person', tone: 'text-info', metric: null };

  const entries = value && typeof value === 'object' && !Array.isArray(value) ? Object.entries(value) : value === undefined ? [] : [[null, value]];
  const words = entries.filter(([, v]) => !isNum(v));
  const nums = entries.filter(([, v]) => isNum(v));
  const show = (list) => list.map(([k, v]) => (k && list.length > 1 ? `${k} ${fmt(v)}` : fmt(v))).join(' · ');

  // What the instrument measured, when it measured something.
  let metric = null;
  if (d.check === 'grounded' && d.claims != null) metric = `${d.supported} of ${d.claims} claims`;
  else if (isNum(d.probability)) metric = `confidence ${two(d.probability)}`;
  else if (d.op === 'similarity' && d.similarity != null) metric = `similarity ${two(d.similarity)}`;
  else if (isNum(d.entail)) metric = `confidence ${two(d.entail)}`;

  const outcome = words.length ? show(words) : nums.length ? show(nums) : '—';
  if (!metric && words.length && nums.length) metric = show(nums);
  return { outcome, tone: 'text-[color:var(--text-primary)]', metric };
}

const STEP_DOT = { ok: 'bg-success', errored: 'bg-warning', pending: 'bg-info' };

/** The walk as a table: each step, what it answered, what it measured; the verdict last. */
function Walk({ v }) {
  if (!v.steps?.length) return <div className="py-1 text-[11px] text-[color:var(--text-muted)]">No steps were recorded for this verdict.</div>;
  const nameOf = (id) => v.node_names?.[id] || id;
  const cell = 'px-2 py-1 align-top';
  return (
    <table data-testid="eval3-path" className="my-1.5 w-full table-fixed text-left text-[11.5px]">
      <thead>
        <tr className="text-[10.5px] uppercase tracking-wider text-[color:var(--text-muted)]">
          <th scope="col" className={`${cell} w-[40%] font-semibold`}>Step</th>
          <th scope="col" className={`${cell} font-semibold`}>Outcome</th>
          <th scope="col" className={`${cell} w-[34%] font-semibold`}>Metric</th>
        </tr>
      </thead>
      <tbody>
        {v.steps.map((st, i) => {
          const c = stepCells(st);
          const state = st.error || st.state === 'errored' ? 'errored' : st.state === 'pending' ? 'pending' : 'ok';
          return (
            <tr key={st.id ?? st.seq ?? i} className="border-t border-[color:var(--border-subtle)]" data-testid="eval3-step">
              <td className={`${cell} max-w-0`}>
                <span className="flex min-w-0 items-center gap-1.5">
                  <span aria-hidden className={`h-1.5 w-1.5 shrink-0 rounded-full ${STEP_DOT[state]}`} />
                  <span className="truncate text-[color:var(--text-primary)]" title={nameOf(st.node_id)}>{nameOf(st.node_id)}</span>
                </span>
              </td>
              <td className={`${cell} max-w-0`}><span className={`block truncate font-mono text-[11px] ${c.tone}`} title={c.title || c.outcome}>{c.outcome}</span></td>
              <td className={`${cell} max-w-0`}>
                <span className="block truncate tabular-nums text-[color:var(--text-secondary)]" title={c.metric || undefined}>{c.metric || '—'}</span>
              </td>
            </tr>
          );
        })}
        <tr className="border-t border-[color:var(--border-default)]">
          <td className={`${cell} font-medium text-[color:var(--text-secondary)]`}>Result</td>
          <td className={`${cell} max-w-0`} colSpan={2}><VerdictChip v={v} /></td>
        </tr>
      </tbody>
    </table>
  );
}

/** A verdict marked wrong from a trace view opens Ask beside it, drafted like the results page's. */
function askAboutMarkedWrong(v, name, f) {
  const path = v.path || [];
  const end = path[path.length - 1];
  const ending = v.state === 'resolved' && end && v.node_names?.[end] && v.node_names[end] !== end ? v.node_names[end] : null;
  const said = [v.passed ? 'Pass' : 'Fail', ending, decisionText(v), v.reason_code].filter(Boolean).join(' · ');
  const kind = v.target_kind || (v.span_id ? 'span' : v.conversation_id ? 'conversation' : v.run_id ? 'run' : null);
  const unitId = kind === 'span' ? v.span_id : kind === 'conversation' ? v.conversation_id : v.run_id;
  openAsk(markedWrongPrompt({
    lineage: { name, target: kind },
    results: [{
      id: v.id, said, version: v.version, path: path.map((id) => v.node_names?.[id] || id).join(' → '),
      unit: kind && unitId ? `${kind} ${unitId}` : null, correctedPassed: f?.corrected_passed ?? !v.passed, reason: f?.reason_text || '',
    }],
  }));
}

function VerdictRow({ teamId, team, v, name, nth, feedback, onFeedback }) {
  const [open, setOpen] = useState(false);
  const results = team ? `/${encodeURIComponent(team)}/agent-analytics/evals?eval=${v.lineage_id}&mode=results` : null;
  return (
    <>
      <tr className={tableRowClass} data-testid="eval3-verdict" onClick={() => setOpen((o) => !o)}>
        <td className={`${tableDataCellClass} max-w-0`}>
          <button type="button" aria-expanded={open} aria-label={`${name} path`} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            className="flex w-full min-w-0 items-center gap-1 text-left">
            {open ? <ChevronDown size={12} className="shrink-0 text-[color:var(--text-muted)]" /> : <ChevronRight size={12} className="shrink-0 text-[color:var(--text-muted)]" />}
            <span className="truncate font-medium" title={name}>{name}</span>
            {nth && <span className="shrink-0 text-[10.5px] tabular-nums text-[color:var(--text-muted)]" title={`The ${nth} call of this span`}>#{nth}</span>}
          </button>
        </td>
        <td className={`${tableDataCellClass} max-w-0`}><VerdictChip v={v} /></td>
        <td className={tableDataCellClass} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1.5">
            {v.state === 'resolved' && <Eval3VerdictFeedback teamId={teamId} verdictId={v.id} value={feedback} onChange={(f) => onFeedback(v.id, f)} onMarkedWrong={(f) => askAboutMarkedWrong(v, name, f)} />}
            {results && (
              <a href={results} title="Open this eval's results" aria-label={`Open ${name} results`}
                className="shrink-0 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">
                <ExternalLink size={12} />
              </a>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-[color:var(--border-subtle)]">
          <td colSpan={3} className="px-3 pb-1"><Walk v={v} /></td>
        </tr>
      )}
    </>
  );
}

/** One eval's newest verdict on this unit: which version ran is the eval's business, not this list's. */
function LineageRow({ lineage, ...rest }) {
  const newest = lineage.verdicts[0];
  if (!newest) return null;
  return <VerdictRow {...rest} v={newest} name={lineage.name} feedback={rest.feedback[newest.id]} />;
}

/** A labelled group inside the table: a span name, or a turn of the conversation. */
function GroupRow({ label, sub, depth = 0 }) {
  return (
    <tr className="h-8 border-b border-[color:var(--border-subtle)]">
      <td colSpan={3} className="px-3 text-[10.5px] text-[color:var(--text-muted)]" style={{ paddingLeft: 12 + depth * 12 }}>
        <span className="font-medium text-[color:var(--text-secondary)]">{label}</span>
        {sub && <span> · {sub}</span>}
      </td>
    </tr>
  );
}

/**
 * Spans grouped under their name, in the order they first ran. A name that
 * ran more than once numbers its rows (#1, #2) so the rows stay apart.
 */
export function spansByName(spans) {
  const groups = new Map();
  for (const sp of spans) {
    const name = sp.name || 'span';
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(sp);
  }
  return [...groups.entries()].map(([name, list]) => ({ name, spans: list }));
}

function VerdictTable({ label, children }) {
  return (
    <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface" aria-label={label}>
      <div className="overflow-x-auto">
      <table className="w-full table-fixed text-left">
        <thead>
          <tr className={tableHeaderRowClass}>
            <th className={`${tableHeaderCellClass} w-[44%]`}>Eval</th>
            <th className={tableHeaderCellClass}>Result</th>
            <th className={`${tableHeaderCellClass} w-[76px] text-right`} title="Was this eval right about this?">Correct?</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      </div>
    </div>
  );
}

/** Every eval a verdicts response shows: the unit's own, and those of the runs and spans inside it. */
export function countVerdicts(d) {
  if (!d) return 0;
  const spansOf = (x) => (Array.isArray(x?.spans) ? x.spans : []);
  const nested = [...spansOf(d), ...(d.thread_runs || []).flatMap((r) => [r, ...spansOf(r)])];
  return (d.lineages || []).length + nested.reduce((n, x) => n + (x.lineages || []).length, 0);
}

/** The count for a Scores badge, fetched before the tab is opened (the panel only loads once it is). */
/**
 * One request per unit, shared. The Scores badge asks as the trace opens;
 * the panel, mounted when the tab is opened, reads the same answer — already
 * there, so the new evals show at once beside the older ones instead of
 * after a second request of their own. A newer refreshKey (a grade) asks again.
 */
const SHARED_TTL_MS = 30 * 1000;
const shared = new Map();   // key → { at, promise, data }
const keyOf = (teamId, target, unitId, refreshKey) => `${teamId}|${target}|${unitId}|${refreshKey ?? ''}`;

export function loadUnitVerdicts(teamId, target, unitId, refreshKey) {
  const key = keyOf(teamId, target, unitId, refreshKey);
  const hit = shared.get(key);
  if (hit && Date.now() - hit.at < SHARED_TTL_MS) return hit.promise;
  const entry = { at: Date.now(), data: null };
  entry.promise = new Promise((resolve) => resolve(getUnitVerdicts(teamId, target, unitId)))
    .then((d) => { entry.data = d; return d; })
    .catch((e) => { shared.delete(key); throw e; });
  shared.set(key, entry);
  if (shared.size > 200) shared.delete(shared.keys().next().value);
  return entry.promise;
}

/** The answer when it has already arrived, for a first render with no wait. */
function sharedNow(teamId, target, unitId, refreshKey) {
  const hit = shared.get(keyOf(teamId, target, unitId, refreshKey));
  return hit && hit.data && Date.now() - hit.at < SHARED_TTL_MS ? hit.data : null;
}

/** For tests. */
export function _resetShared() { shared.clear(); }

export function useEval3Count(teamId, target, unitId, refreshKey) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!teamId || !unitId || !target) { setCount(0); return undefined; }
    let cancelled = false;
    loadUnitVerdicts(teamId, target, unitId, refreshKey)
      .then((d) => { if (!cancelled) setCount(countVerdicts(d)); })
      .catch(() => { if (!cancelled) setCount(0); });
    return () => { cancelled = true; };
  }, [teamId, target, unitId, refreshKey]);
  return count;
}

export default function Eval3VerdictsPanel({ teamId, target, unitId, refreshKey, onCount, title = 'Evals' }) {
  const { team } = useParams();
  const shape = (d) => {
    const spansOf = (x) => (Array.isArray(x?.spans) ? x.spans : []);
    return { ...d, lineages: d.lineages || [], spans: spansOf(d), thread_runs: (d.thread_runs || []).map((r) => ({ ...r, spans: spansOf(r) })) };
  };
  const [data, setData] = useState(() => {
    const ready = sharedNow(teamId, target, unitId, refreshKey);
    return ready ? shape(ready) : null;
  });
  const [feedback, setFeedback] = useState({});
  const [error, setError] = useState(null);
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;

  useEffect(() => {
    if (!teamId || !unitId || !target) return undefined;
    let cancelled = false;
    loadUnitVerdicts(teamId, target, unitId, refreshKey)
      .then(async (d) => {
        if (cancelled) return;
        const shaped = shape(d);
        setData(shaped);
        setError(null);
        // Everything shown counts: the unit's own evals, and those of the runs and spans inside it.
        const nested = [...shaped.spans, ...shaped.thread_runs.flatMap((r) => [r, ...r.spans])];
        const allLineages = [...shaped.lineages, ...nested.flatMap((x) => x.lineages || [])];
        onCountRef.current?.(countVerdicts(shaped));
        const all = allLineages.flatMap((l) => l.verdicts);
        const ids = all.filter((v) => v.state === 'resolved').map((v) => v.id);
        if (!ids.length) return;
        try {
          const fb = await getMyFeedback(teamId, ids);
          if (!cancelled) setFeedback(fb);
        } catch (_) { /* the thumbs render empty; the verdicts still show */ }
      })
      .catch((e) => { if (!cancelled) { setData(null); setError(e?.response?.data?.error || e.message); } });
    return () => { cancelled = true; };
  }, [teamId, target, unitId, refreshKey]);

  if (error) {
    return <p className="text-[11px] text-[color:var(--text-muted)]">Evals could not load: {error}</p>;
  }
  if (!data || (!data.lineages.length && !data.thread_runs.length && !data.spans.length)) return null;

  const rowProps = {
    teamId, team, feedback,
    onFeedback: (id, f) => setFeedback((m) => ({ ...m, [id]: f })),
  };

  const heading = 'text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]';
  const rows = (lineages, key = '', nth = null) => lineages.map((l) => <LineageRow key={`${key}${l.lineage_id}`} lineage={l} nth={nth} {...rowProps} />);
  // Under a span name, one eval's rows sit together — its #1, #2, #3 — then the next eval's.
  const spanRows = (spans, depth, keyPrefix = '') => spansByName(spans).flatMap((g) => {
    const many = g.spans.length > 1;
    const byEval = new Map();
    g.spans.forEach((sp, i) => {
      for (const l of sp.lineages) {
        if (!byEval.has(l.lineage_id)) byEval.set(l.lineage_id, []);
        byEval.get(l.lineage_id).push({ l, sp, nth: many ? i + 1 : null });
      }
    });
    return [
      <GroupRow key={`g-${keyPrefix}${g.name}`} label={g.name} sub={many ? `${g.spans.length} calls` : null} depth={depth} />,
      ...[...byEval.values()].flat().map(({ l, sp, nth }) => <LineageRow key={`${sp.span_id}-${l.lineage_id}`} lineage={l} nth={nth} {...rowProps} />),
    ];
  });

  return (
    <section className="flex flex-col gap-2" data-testid="eval3-verdicts" aria-label={title}>
      <p className={heading}>{title}</p>
      <VerdictTable label={title}>
        {rows(data.lineages)}
        {data.spans.length > 0 && (
          <>
            <tr aria-label="Spans in this run"><td colSpan={3} className={`${heading} px-3 pb-1 pt-3`}>Spans in this run</td></tr>
            {spanRows(data.spans, 0)}
          </>
        )}
        {data.thread_runs.length > 0 && (
          <>
            <tr aria-label="Runs in this conversation"><td colSpan={3} className={`${heading} px-3 pb-1 pt-3`}>Runs in this conversation</td></tr>
            {data.thread_runs.flatMap((r, i) => [
              <GroupRow key={`r-${r.run_id}`} label={r.turn ? `Turn ${r.turn}` : `Run ${i + 1}`} />,
              ...rows(r.lineages, `${r.run_id}-`),
              ...(r.spans.length ? [<tr key={`s-${r.run_id}`} aria-label="Spans in this run"><td colSpan={3} className="px-3 pt-1 text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]" style={{ paddingLeft: 24 }}>Spans in this run</td></tr>] : []),
              ...spanRows(r.spans, 1, `${r.run_id}-`),
            ])}
          </>
        )}
      </VerdictTable>
    </section>
  );
}
